// src/services/auth.ts
//
// Auth-domain service. Wraps the network calls behind typed functions so
// callers (signin page, OTP page, AuthContext) deal in domain values
// (nutritionistId, nutritionistData) instead of raw Response objects + JSON
// parsing + success-flag inspection.
//
// On a successful OTP verify, this module also writes the access/refresh
// tokens into the token store — there is no path where a caller would want
// the verified payload without persisting the tokens, and ordering the
// setTokens call inside the service eliminates an entire class of
// "forgot to store the token before triggering the next authenticated
// request" bugs.

import { apiFetch } from "../Component/api/apiFetch";
import { setTokens } from "../Component/api/tokenStore";
import { API_URLS } from "../Component/api/url";
import { ApiError, handleJson } from "./http";

// TODO: replace `any` with a full NutritionistApiPayload interface once the
// backend response shape is locked. Kept as plain `any` so callers (login(),
// AuthContext mapping) can use the payload without a structural type clash —
// `Record<string, any>` is too strict to assign to `User` which has required
// `nutrist_id` / `nutrist_email` fields.
export type NutritionistApiPayload = any;

interface SigninResponse {
  Success?: string;
  nutrist_id?: number;
  message?: string;
}

interface VerifyOtpResponse {
  success?: string;
  access_token?: string;
  refresh_token?: string;
  nutritionist_data?: NutritionistApiPayload;
  message?: string;
}

interface NutritionistDetailsResponse {
  success?: string | boolean;
  // Newer endpoint shape returns `data`; legacy callers return `nutritionist_data`.
  data?: NutritionistApiPayload;
  nutritionist_data?: NutritionistApiPayload;
  message?: string;
}

// POST /login_nutritionist — triggers the OTP email. Returns the nutritionist
// id needed by the OTP screen.
export async function signin(
  emailOrMobile: string
): Promise<{ nutritionistId: number }> {
  const data = await handleJson<SigninResponse>(
    await apiFetch(API_URLS.SIGNIN, {
      method: "POST",
      skipAuth: true,
      body: JSON.stringify({ email_or_mobile: emailOrMobile }),
    })
  );

  // Backend uses capital-S "Success" here (deliberate, not a typo).
  if (data.Success !== "1" || typeof data.nutrist_id !== "number") {
    throw new ApiError(200, data.message || "Failed to send OTP", data);
  }
  return { nutritionistId: data.nutrist_id };
}

// POST /nutritionist_verify_otp — exchanges the OTP for tokens + profile data.
// Side effect on success: writes tokens to the token store.
export async function verifyOtp(
  otp: string,
  nutritionistId: string | number
): Promise<{ nutritionistData: NutritionistApiPayload }> {
  const data = await handleJson<VerifyOtpResponse>(
    await apiFetch(API_URLS.VERIFY_OTP, {
      method: "POST",
      skipAuth: true,
      body: JSON.stringify({
        otp,
        nutritionist_id: String(nutritionistId),
      }),
    })
  );

  if (data.success !== "1" || !data.nutritionist_data) {
    throw new ApiError(200, data.message || "Invalid OTP", data);
  }

  setTokens(data.access_token ?? null, data.refresh_token ?? null);
  return { nutritionistData: data.nutritionist_data };
}

// GET /get_nutritionists/:id — full profile + RBAC permissions for an
// arbitrary nutritionist by id (used by the team management view page).
// Normalises the two response shapes the endpoint can return (`data` for
// the newer payload, `nutritionist_data` for the legacy one).
export async function getNutritionistFullDetails(
  nutristId: number
): Promise<NutritionistApiPayload> {
  const data = await handleJson<NutritionistDetailsResponse>(
    await apiFetch(API_URLS.GET_NUTRITIONIST_FULL_DETAILS(nutristId), {
      method: "GET",
    })
  );

  const isSuccess = data.success === "1" || data.success === true;
  const payload = data.data ?? data.nutritionist_data;
  if (!isSuccess || !payload) {
    throw new ApiError(
      200,
      data.message || "Failed to load nutritionist details",
      data
    );
  }
  return payload;
}

// GET /get_my_profile — full profile + RBAC permissions for the currently
// logged-in nutritionist (identified by the access token, no id needed).
// Same normalisation as getNutritionistFullDetails so AuthContext can treat
// the returned payload identically.
export async function getMyProfile(): Promise<NutritionistApiPayload> {
  const data = await handleJson<NutritionistDetailsResponse>(
    await apiFetch(API_URLS.GET_PROFILE, { method: "GET" })
  );

  const isSuccess = data.success === "1" || data.success === true;
  const payload = data.data ?? data.nutritionist_data;
  if (!isSuccess || !payload) {
    throw new ApiError(200, data.message || "Failed to load profile", data);
  }
  return payload;
}
